import { useMemo } from 'react';

import { useCardDefinitions } from '../contexts/CardDefinitionsContext';
import { createLookupResolver } from '../utils/lookups';
import { normalizeId } from '../utils/ids';
import { cardPagePath } from '../utils/cards';
import getItemDisplayValue from '../utils/items';
import { owningCollectionIdOf } from '../utils/recordQualifier';
import useLinkedItems from './useLinkedItems';

/**
 * Turns raw `/api/views/recent` entries into displayable rows the command
 * palette and the History page share:
 *   { key, id, targetType, kind, title, path, card, collection_id, owner, viewedAt }
 *
 * `owner` is the collection that owns the record (an item's, a collection
 * card's) — what its badge is qualified with on these global surfaces
 * (RecordBadge `collection`); null for a collection, or a global card.
 *
 * Item titles route through the ONE lookup convention (createLookupResolver
 * + useLinkedItems), so a display template that leads with a card-lookup
 * renders its label, not a raw id.
 */
export default function useResolvedViewHistory(views) {
	const { cardsById, getElementsForCard, loading: cardsLoading } = useCardDefinitions();

	const itemTargets = useMemo(
		() => (views || [])
			.filter(view => view?.targetType === 'item' && view?.target)
			.map(view => view.target),
		[views]
	);

	const resolvedItemsById = useLinkedItems({
		items: itemTargets,
		getElementsForCard,
		enabled: !cardsLoading,
		resetKey: 'view-history',
	});

	const resolveLookupValue = useMemo(() => createLookupResolver({
		itemsById: resolvedItemsById,
		cardsById,
		getElementsForCard,
	}), [resolvedItemsById, cardsById, getElementsForCard]);

	return useMemo(() => (views || []).map(view => {
		const id = normalizeId(view?.target_id);
		const target = view?.target;
		if (!id || !target) return null;

		if (view.targetType === 'collection') {
			return {
				key: `view-collection-${id}`,
				id,
				targetType: 'collection',
				kind: 'Collection',
				title: target.name || 'Untitled collection',
				path: `/collections/${id}/items`,
				// The collection record itself carries the badge's icon/color.
				card: target,
				collection_id: null,
				owner: null,
				viewedAt: view.viewedAt,
			};
		}
		if (view.targetType === 'card') {
			const card = cardsById[id] || target;
			return {
				key: `view-card-${id}`,
				id,
				targetType: 'card',
				kind: 'Card',
				title: target.name || 'Untitled card',
				path: cardPagePath({ ...card, _id: id }),
				card,
				collection_id: null,
				owner: owningCollectionIdOf(card, 'card'),
				viewedAt: view.viewedAt,
			};
		}
		if (view.targetType === 'item') {
			const collectionId = normalizeId(view.collection_id || target.collection_id);
			if (!collectionId) return null;
			const card = cardsById[normalizeId(target.card_id)];
			const title = getItemDisplayValue({
				card,
				elementDefs: getElementsForCard(target.card_id),
				item: target,
				resolveLookupValue,
			});
			return {
				key: `view-item-${id}`,
				id,
				targetType: 'item',
				kind: 'Item',
				title: title || 'Untitled item',
				path: `/collections/${collectionId}/items?item=${id}`,
				card: card || null,
				collection_id: collectionId,
				owner: collectionId,
				viewedAt: view.viewedAt,
			};
		}
		return null;
	}).filter(Boolean), [views, cardsById, getElementsForCard, resolveLookupValue]);
}
